import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, UserCheck, Circle } from 'lucide-react';
import { RecallStatus } from '@/hooks/useRecallAssignmentStatus';
import { cn } from '@/lib/utils';

interface RecallStatusBadgeProps {
  status: RecallStatus;
  technicianName?: string | undefined;
  expectedVisitDate?: string | undefined;
  size?: 'sm' | 'default' | 'lg';
  showIcon?: boolean;
  showText?: boolean;
  className?: string;
}

export const RecallStatusBadge: React.FC<RecallStatusBadgeProps> = ({
  status,
  technicianName,
  expectedVisitDate,
  size = 'default',
  showIcon = true,
  showText = true,
  className
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'available':
        return {
          label: 'Available',
          variant: 'outline' as const,
          icon: Circle,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border'
        };
      case 'assigned':
        return {
          label: 'Assigned',
          variant: 'default' as const,
          icon: UserCheck,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200'
        };
      case 'confirmed':
        return {
          label: 'Confirmed',
          variant: 'default' as const,
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      default:
        return {
          label: 'Unknown',
          variant: 'outline' as const,
          icon: Circle,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border'
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    default: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4'
  };

  // Format the date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Build tooltip content
  const tooltipContent = () => {
    const parts = [];
    if (technicianName) {
      parts.push(`Assigned to: ${technicianName}`);
    }
    if (expectedVisitDate) {
      parts.push(`Expected: ${formatDate(expectedVisitDate)}`);
    }
    return parts.length > 0 ? parts.join(' • ') : '';
  };

  return (
    <Badge
      variant={config.variant}
      className={cn(
        sizeClasses[size],
        config.bgColor,
        config.borderColor,
        'inline-flex items-center gap-1.5 font-medium',
        className
      )}
      title={tooltipContent()}
    >
      {showIcon && <Icon className={cn(iconSizes[size], config.color)} />}
      {showText && <span className={config.color}>{config.label}</span>}
    </Badge>
  );
};

export default RecallStatusBadge;
